import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import RichTextEditor from "@/components/RichTextEditor";
import RichText from "@/components/RichText";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";

const empty = { day: "", name: "", description: "", start_time: "", end_time: "", venue: "", google_maps_link: "", display_order: 0 };

export default function Schedule() {
  const { eventId } = useOutletContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/schedule/${eventId}`);
    setItems(data);
    setLoading(false);
  };
  useEffect(() => {
    if (eventId) load();
  }, [eventId]);

  const openNew = () => {
    setForm({ ...empty, display_order: items.length + 1 });
    setEditId(null);
    setOpen(true);
  };
  const openEdit = (i) => {
    setForm({ ...i });
    setEditId(i.id);
    setOpen(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, event_id: eventId, display_order: Number(form.display_order) || 0 };
      if (editId) await api.put(`/schedule/${editId}`, payload);
      else await api.post("/schedule", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };
  const remove = async (id) => {
    await api.delete(`/schedule/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div data-testid="schedule-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Schedule</h1>
          <p className="text-sm text-[#525252]">{items.length} events</p>
        </div>
        <Button onClick={openNew} data-testid="add-schedule-button" className="bg-[#1A362D] hover:bg-[#12271f]">
          <Plus className="mr-2 h-4 w-4" /> Add Event
        </Button>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#1A362D]" /></div>
      ) : (
        <div className="space-y-3">
          {items.map((i) => (
            <div key={i.id} className="flex items-start justify-between rounded-md border border-[#E5E5E5] bg-white p-5" data-testid={`schedule-row-${i.id}`}>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-[#1A362D]">{i.day}</div>
                <div className="text-lg font-semibold">{i.name}</div>
                <div className="mt-1 text-sm text-[#525252]">
                  {i.description ? <RichText html={i.description} className="text-sm" /> : null}
                </div>
                <div className="mt-2 flex flex-wrap gap-4 text-xs text-[#525252]">
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {i.start_time} – {i.end_time}</span>
                  {i.venue && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {i.venue}</span>}
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(i)} data-testid={`edit-schedule-${i.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(i.id)} data-testid={`delete-schedule-${i.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="rounded-md border border-[#E5E5E5] bg-white p-10 text-center text-[#525252]">No events scheduled.</div>}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="schedule-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Event" : "Add Event"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2"><Label>Event Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="schedule-name-input" /></div>
            <div className="col-span-2 space-y-2"><Label>Day</Label><Input value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })} placeholder="Saturday, Dec 13" /></div>
            <div className="col-span-2 space-y-2">
              <Label>Description</Label>
              <RichTextEditor
                key={`schedule-desc-${editId || "new"}`}
                value={form.description || ""}
                onChange={(html) => setForm({ ...form, description: html })}
                minHeight="100px"
                data-testid="schedule-description-editor"
              />
            </div>
            <div className="space-y-2"><Label>Start Time</Label><Input value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} placeholder="19:00" /></div>
            <div className="space-y-2"><Label>End Time</Label><Input value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} placeholder="23:00" /></div>
            <div className="col-span-2 space-y-2"><Label>Venue</Label><Input value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} /></div>
            <div className="col-span-2 space-y-2"><Label>Google Maps Link</Label><Input value={form.google_maps_link} onChange={(e) => setForm({ ...form, google_maps_link: e.target.value })} /></div>
            <div className="space-y-2"><Label>Display Order</Label><Input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name} className="bg-[#1A362D] hover:bg-[#12271f]" data-testid="save-schedule-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
